import {Translatable} from 'common/i18n';
import {
  BaseTimeUnit,
  ExtendedTimeUnit,
  NonStandardTimeUnit,
  SpecialTimeUnit,
  TimeUnit as GeneratedTimeUnit,
} from 'common/types/reports/bookmark';

export type StandardTimeUnit = BaseTimeUnit | ExtendedTimeUnit;

export const TimeUnit = Object.assign(BaseTimeUnit, ExtendedTimeUnit, NonStandardTimeUnit, SpecialTimeUnit);
export type TimeUnit = GeneratedTimeUnit;
export const OrderedStandardTimeUnits: StandardTimeUnit[] = [
  TimeUnit.Minute,
  TimeUnit.Hour,
  TimeUnit.Day,
  TimeUnit.Week,
  TimeUnit.Month,
  TimeUnit.Quarter,
  TimeUnit.Year,
];

/* eslint-disable @typescript-eslint/naming-convention */
/** @lintignore members are accessed dynamically via TimeUnitLabels[unitValue] */
export enum TimeUnitLabels {
  minute = `Minute`,
  hour = `Hour`,
  day = `Day`,
  week = `Week`,
  month = `Month`,
  quarter = `Quarter`,
  year = `Year`,
  hour_of_day = `Hour of Day`,
  day_of_week = `Day of Week`,
  session = `Session`,
  second = `Second`,
}
export const TimeUnitLabelTranslatable: {[key in TimeUnit]: (count: number | undefined) => Translatable} = {
  [TimeUnit.Minute]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.minute`, {count}),
  [TimeUnit.Hour]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.hour`, {count}),
  [TimeUnit.Day]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.day`, {count}),
  [TimeUnit.Week]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.week`, {count}),
  [TimeUnit.Month]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.month`, {count}),
  [TimeUnit.Quarter]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.quarter`, {count}),
  [TimeUnit.Year]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.year`, {count}),
  [TimeUnit.HourOfDay]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.hour-of-day`, {count}),
  [TimeUnit.DayOfWeek]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.day-of-week`, {count}),
  [TimeUnit.Session]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.session`, {count}),
  [TimeUnit.Second]:
    (count) =>
    ({t}) =>
      t(`reports.time-unit.second`, {count}),
};
/* eslint-enable */

export interface DateRangeAttributes {
  exclusionOffset?: ExclusionOffset | null;
  from?: BaseDate | null;
  rolling?: boolean;
  to?: BaseDate | null;
  type: DateRangeType;
  window?: RelativeDate;
}

export type DateType = `absolute` | `relative` | `special_literal` | `null`;

export interface RelativeDate {
  unit: TimeUnit;
  value: number;
}

/**
 * string formats:
 *  "YYYY-MM-dd"
 *  "$date_type_description" e.g. `$start_of_today`
 */
export type BaseDate = RelativeDate | string;

export interface ApiDateRange {
  before?: boolean;
  from_date?: BaseDate;
  to_date?: BaseDate;
  window?: RelativeDate;
}

export interface BookmarkDateRange {
  type: DateRangeType;
  from_date: BaseDate;
  to_date: BaseDate;
  exclusion_offset: ExclusionOffset | null;
}

export interface CalendarDateRange {
  from?: string;
  to?: string;
}

export enum DateRangeType {
  Between = `between`,
  NotBetween = `not between`,
  On = `on`,
  NotOn = `not on`,
  RelativeAfter = `in the last`,
  NotRelativeAfter = `not in the last`,
  RelativeBefore = `before the last`,
  RelativeFuture = `in the next`,
  Since = `since`,
  Before = `before`,
  StartsOn = `starts on`,
  EndsOn = `ends on`,
}

export enum DisplayContext {
  Default = `default`,
  FullLength = `full_length`,
  ImpactTimePicker = `impact_time_picker`,
  PropertyFilter = `property_filter`,
  RelativeRange = `relative_range`,
  ExperimentTimePicker = `experiment_time_picker`,
  ExperimentCompletedTimePicker = `experiment_completed_time_picker`,
  UnifiedWithPresets = `unified_with_presets`,
}

export const INCOMPLETE_PERIOD_OFFSET: string = `$incomplete_period`;
export type ExclusionOffset = RelativeDate | typeof INCOMPLETE_PERIOD_OFFSET;

export enum ExclusionPreset {
  Today = `today`,
  CurrentWeek = `current_week`,
  CurrentMonth = `current_month`,
  CurrentQuarter = `current_quarter`,
  IncompletePeriod = `incomplete_period`,

  // Deselection state
  NoExclusion = `no_exclusion`,
  // Custom exclusion window
  Custom = `custom`,
}

export enum DisabledReason {
  MTAMaxQueryDays = `mta_max_query_days`, // multi-touch attribution queries
  AllEventsMaxQueryDays = `all_events_max_query_days`,
  DAUMaxQueryDays = `dau_max_query_days`,
  DataHistoryDays = `data_history_days`,
  InvertedDates = `inverted_dates`,
}
